"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, ShoppingCart, Plus, Minus } from "lucide-react";

const colors = [
  {
    name: "Brick",
    tileImage: "/images/home/available-colors/brick.png",
    exampleImage: "/images/home/available-colors/brick-example.jpeg",
  },
  {
    name: "Downing Sands",
    tileImage: "/images/home/available-colors/downing-sands.png",
    exampleImage: "/images/home/available-colors/downing-sands-example.jpeg",
  },
  {
    name: "WhirlyBird",
    tileImage: "/images/home/available-colors/whirlybird.png",
    exampleImage: "/images/home/available-colors/whirlybird-example.jpeg",
  },
  {
    name: "Golden Harvest",
    tileImage: "/images/home/available-colors/golden-harvest.png",
    exampleImage: "/images/home/available-colors/golden-harvest-example.jpeg",
  },
  {
    name: "Super Nova",
    tileImage: "/images/home/available-colors/super-nova.png",
    exampleImage: "/images/home/available-colors/super-nova-example.jpeg",
  },
  {
    name: "Gray Wool",
    tileImage: "/images/home/available-colors/gray-wool.png",
    exampleImage: "/images/home/available-colors/gray-wool-example.jpeg",
  },
  {
    name: "Light Taupe",
    tileImage: "/images/home/available-colors/light-taupe.png",
    exampleImage: "/images/home/available-colors/light-taupe-example.jpeg",
  },
  {
    name: "Terracotta",
    tileImage: "/images/home/available-colors/terracotta.png",
    exampleImage: "/images/home/available-colors/terracotta-example.jpeg",
  },
  {
    name: "Dark Jade",
    tileImage: "/images/home/available-colors/dark-jade.png",
    exampleImage: "/images/home/available-colors/dark-jade-example.jpeg",
  },
];

interface HeroConfiguratorProps {
  basePrice?: number;
}

export function ConcrettaHeroConfigurator({
  basePrice = 89.99,
}: HeroConfiguratorProps) {
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(colors[0].name);
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);

  const totalPrice = basePrice * quantity;

  const selectedColorData =
    colors.find((c) => c.name === selectedColor) || colors[0];

  const handleAddToCart = () => {
    // console.log("Adding to cart:", {
    //   quantity,
    //   color: selectedColor,
    //   price: totalPrice,
    // });
  };

  return (
    <section
      id="concretta-configurator"
      data-header-scheme="dark"
      className="pt-32 pb-12 bg-foreground/5 dark:bg-card/30 transition-colors"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="aspect-square bg-card border border-border rounded-lg overflow-hidden shadow-xl mb-4 relative">
              <Image
                src={selectedColorData.exampleImage}
                alt={`Concretta Microcement - ${selectedColorData.name}`}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className="object-cover transition-all duration-500"
                quality={90}
              />
            </div>
            <div className="grid grid-cols-5 gap-3">
              {colors.map((color, i) => (
                <button
                  key={color.name}
                  onClick={() => {
                    setSelectedThumbnail(i);
                    setSelectedColor(color.name);
                  }}
                  className={`aspect-square bg-card border rounded-lg overflow-hidden relative hover:ring-2 hover:ring-primary transition-all ${
                    selectedThumbnail === i
                      ? "ring-2 ring-primary"
                      : "border-border"
                  }`}
                >
                  <Image
                    src={color.exampleImage}
                    alt={`${color.name} thumbnail`}
                    fill
                    sizes="100px"
                    className="object-cover"
                  />
                </button>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div>
              <Badge className="mb-2">Premium Quality</Badge>
              <h1 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-4 tracking-tight">
                Concretta Microcement
              </h1>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <span className="text-muted-foreground">(127 reviews)</span>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Premium microcement for creating seamless, durable surfaces with
                an authentic concrete aesthetic. Perfect for modern,
                industrial-style interiors.
              </p>
            </div>
            <div className="text-3xl font-bold text-foreground">
              ${totalPrice.toFixed(2)}
              <span className="text-lg font-normal text-muted-foreground ml-2">
                per gallon
              </span>
            </div>
            <div>
              <h3 className="font-serif text-lg font-medium text-foreground mb-3 tracking-tight">
                Color
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {colors.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(color.name)}
                    className={`flex flex-col items-center gap-2 p-3 border rounded-lg transition-all ${
                      selectedColor === color.name
                        ? "border-primary bg-primary/10 ring-2 ring-primary"
                        : "border-border hover:border-foreground/50"
                    }`}
                  >
                    <div className="w-full aspect-square relative rounded overflow-hidden">
                      <Image
                        src={color.tileImage}
                        alt={`${color.name} tile`}
                        fill
                        sizes="100px"
                        className="object-cover"
                      />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {color.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-lg font-medium text-foreground mb-3 tracking-tight">
                Quantity
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 hover:bg-muted transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="px-4 py-2 font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-muted-foreground">gallons</span>
              </div>
            </div>
            <Button onClick={handleAddToCart} size="lg" className="w-full">
              <ShoppingCart className="mr-2 h-4 w-4" />
              Add to Cart - ${totalPrice.toFixed(2)}
            </Button>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>✓ Free shipping on orders over $500</p>
              <p>✓ 30-day satisfaction guarantee</p>
              <p>✓ Professional application support included</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
